import { Route, Routes } from 'react-router-dom';
import { useEffect, lazy, Suspense } from 'react';
import { DefaultSEO, RouteSEOWatcher, StaticRouteSEO } from './components/SEO';
import { firebaseConfig } from './components/auth/firebaseConfig';
import { initializeApp } from 'firebase/app';
// Clarity / react-ga4 / react-facebook-pixel se importan de forma DINÁMICA dentro del
// effect diferido: (1) así no se cargan en el bundle inicial (mejor LCP) y (2) no se
// evalúan al importar <App/> en Node durante el SSR del build.
import './translates/i18n';
import useCheckInfo from './CheckInfo';
import { ScrollToTop } from './utils/ScrollToTop';
import { isEmptyUser } from './utils/session';
import { isValidEmail } from './utils/utils';
import { setupPerformanceMonitoring } from './utils/performance';
import { LawyerThemeWrapper } from './components/LawyerThemeWrapper';
import { PushNotificationBanner } from './components/PushNotificationBanner';
import RouteFallback from '@/components/ui/RouteFallback';

// Home loads eagerly — it's the landing page and the LCP-critical route.
// Everything else is code-split (lazy) so it stays out of the initial bundle.
import Home from './screens/Home';

// Auth screens
const RoleSelection = lazy(() => import('./screens/RoleSelection'));
const SignIn = lazy(() => import('./screens/SignIn'));
const LogIn = lazy(() => import('./screens/LogIn'));
const UserRegistration = lazy(() => import('./screens/user/Registration'));
const LawyerRegistration = lazy(() => import('./screens/lawyer/Registration'));
const LawyerMarketplace = lazy(() => import('./screens/lawyer/Marketplace'));

// LegalBot screens
const LegalBot = lazy(() => import('./screens/LegalBot'));
const LegalBotDashboard = lazy(() => import('./screens/legalbot/Dashboard'));
const LegalBotConversations = lazy(() => import('./screens/legalbot/Conversations'));
const LegalBotCopilot = lazy(() => import('./screens/legalbot/Copilot'));
const LegalBotLawsuitDraftEditor = lazy(() => import('./screens/legalbot/LawsuitDraftEditor'));
const LegalBotContractDraftEditor = lazy(() => import('./screens/legalbot/ContractDraftEditor'));
const LegalBotOficioDraftEditor = lazy(() => import('./screens/legalbot/OficioDraftEditor'));
const LegalBotCases = lazy(() => import('./screens/legalbot/Cases'));
const LegalBotCaseDetails = lazy(() => import('./screens/legalbot/CaseDetails'));
const LegalBotTyC = lazy(() => import('./screens/legalbot/LegalBotTyC'));
const LegalBotPrivacyPolicy = lazy(() => import('./screens/legalbot/LegalBotPrivacyPolicy'));
const LegalBotCookies = lazy(() => import('./screens/legalbot/LegalBotCookies'));
const LegalBotPricing = lazy(() => import('./screens/legalbot/Pricing'));
const LegalBotPaymentFinish = lazy(() => import('./screens/legalbot/PaymentFinish'));
const LegalBotPlan = lazy(() => import('./screens/legalbot/Plan'));
const LegalBotNotifications = lazy(() => import('./screens/legalbot/LegalBotNotifications'));
const LegalBotLawyerConfigurations = lazy(() => import('./screens/legalbot/LawyerConfigurations'));
const LegalBotTemplates = lazy(() => import('./screens/legalbot/Templates'));
const WhatsAppSuccess = lazy(() => import('./screens/legalbot/whatsapp/Success'));
const WhatsAppFailed = lazy(() => import('./screens/legalbot/whatsapp/Failed'));
const InstagramSuccess = lazy(() => import('./screens/legalbot/instagram/Success'));
const InstagramFailed = lazy(() => import('./screens/legalbot/instagram/Failed'));
const FacebookSuccess = lazy(() => import('./screens/legalbot/facebook/Success'));
const FacebookFailed = lazy(() => import('./screens/legalbot/facebook/Failed'));
const FacebookPermisosTutorial = lazy(() => import('./screens/legalbot/facebook/PermisosTutorial'));
const LegalBotInfluencers = lazy(() => import('./screens/legalbot/Influencers'));

const PageLoader = RouteFallback; // skeleton + barra de carga (Abogalia) en vez de blanco

// User screens
const UserCases = lazy(() => import('./screens/user/Cases'));
const UserCaseDetail = lazy(() => import('./screens/user/CaseDetail'));
const CaseCreated = lazy(() => import('./screens/user/CaseCreated'));
const UserProfile = lazy(() => import('./screens/user/Profile'));
const UserConfiguration = lazy(() => import('./screens/user/Configuration'));
const UserChats = lazy(() => import('./screens/user/Chats'));
const UserNewCase = lazy(() => import('./screens/user/NewCase'));

// Lawyer screens
const CaseDetail = lazy(() => import('./screens/lawyer/CaseDetail'));
const LawyerConfiguration = lazy(() => import('./screens/lawyer/Configuration'));
const LawyerPlan = lazy(() => import('./screens/lawyer/Plan'));
const LawyerDirectory = lazy(() => import('./screens/Directory'));
const LawyerDirectoryRanking = lazy(() => import('./screens/lawyer/DirectoryRanking'));
const LawyerChats = lazy(() => import('./screens/lawyer/Chats'));
const PublicLawyerProfile = lazy(() => import('./screens/lawyer/PublicProfile'));
const LawyerRatings = lazy(() => import('./screens/lawyer/Ratings'));
const Clients = lazy(() => import('./screens/lawyer/Clients'));
const EditLawyerProfile = lazy(() => import('./screens/lawyer/EditProfile'));
const AIStudio = lazy(() => import('./screens/lawyer/AIStudio'));
const LawyerJurisprudence = lazy(() => import('./screens/lawyer/Jurisprudence'));

// Lazy load info, blog and location screens for better performance
// Info screens
const HowWorkLawyer = lazy(() => import('./screens/HowWorkLawyer'));
const HowWorkClient = lazy(() => import('./screens/HowWorkClient'));
const Cookies = lazy(() => import('./screens/Cookies'));
const TyC = lazy(() => import('./screens/TyC'));
const Faqs = lazy(() => import('./screens/Faqs'));
const PrivacyPolicy = lazy(() => import('./screens/PrivacyPolicy'));
const AboutUs = lazy(() => import('./screens/AboutUs'));
const Contact = lazy(() => import('./screens/Contact'));
const ForgotPassword = lazy(() => import('./screens/ForgotPassword'));
const NotificationsScreen = lazy(() => import('./screens/Notifications'));
const Pricing = lazy(() => import('./screens/Pricing'));
const PaymentFinish = lazy(() => import('./screens/PaymentFinish'));
const Metrics = lazy(() => import('./screens/Metrics'));
const ValidarAbogados = lazy(() => import('./screens/ValidarAbogados'));
const ConversationInspector = lazy(() => import('./screens/ConversationInspector'));
const NotFound = lazy(() => import('./screens/NotFound'));

// Blog screens
const PaginaDeAbogados = lazy(() => import('./screens/blog/PaginaDeAbogados'));
const AbogadoArgentina = lazy(() => import('./screens/blog/AbogadoArgentina'));
const MejorPaginaAbogadosArgentina = lazy(() => import('./screens/blog/MejorPaginaAbogadosArgentina'));
const MeRecibiDeAbogado = lazy(() => import('./screens/blog/MeRecibiDeAbogado'));
const AbogadosOnline = lazy(() => import('./screens/blog/AbogadosOnline'));
const AbogadoDeFamilia = lazy(() => import('./screens/blog/AbogadoDeFamilia'));
const AbogadoPenalista = lazy(() => import('./screens/blog/AbogadoPenalista'));
const AbogadoLaboral = lazy(() => import('./screens/blog/AbogadoLaboral'));
const AbogadoSucesiones = lazy(() => import('./screens/blog/AbogadoSucesiones'));
const MejoresAbogados = lazy(() => import('./screens/blog/MejoresAbogados'));
const DefensaDelConsumidor = lazy(() => import('./screens/blog/DefensaDelConsumidor'));
const ReclamoFacturaTelecomunicaciones = lazy(() => import('./screens/blog/ReclamoFacturaTelecomunicaciones'));

// Location screens
const LocationPage = lazy(() => import('./screens/locations/LocationPage'));

// SEO programmatic pages
const AbogadoEspecialidadLocalidad = lazy(() => import('./screens/seo/AbogadoEspecialidadLocalidad'));

// Calculadoras (imán de links + leads de alta calidad)
const CalculadoraDespido = lazy(() => import('./screens/tools/CalculadoraDespido'));
const CalculadoraCuotaAlimentaria = lazy(() => import('./screens/tools/CalculadoraCuotaAlimentaria'));
const CalculadoraSucesion = lazy(() => import('./screens/tools/CalculadoraSucesion'));
const CuantoCuestaAbogado = lazy(() => import('./screens/tools/CuantoCuestaAbogado'));

// LegalBot screens (heavy - ~17.5 KiB LegalBotConfigurations + @dnd-kit, ProfileEdit, etc.)
const LegalBotConfigurations = lazy(() => import('./screens/LegalBotConfigurations'));

function App() {
  useEffect(() => {
    // Firebase needed for auth - keep synchronous for initial route protection
    initializeApp(firebaseConfig);

    // Defer analytics/tracking until after LCP to improve FCP/LCP (saves ~400-550ms)
    const deferAnalytics = async () => {
      const [{ default: Clarity }, { default: ReactGA }] = await Promise.all([
        import('@microsoft/clarity'),
        import('react-ga4'),
      ]);
      Clarity.init('q7as44v337');
      ReactGA.initialize('G-9P7NBLNC7T');
      initializeFbPixel();
      setupPerformanceMonitoring();
      // Sentry se carga como chunk aparte y se inicializa fuera del critical path
      initSentry();
    };
    const runWhenIdle = () => {
      if ('requestIdleCallback' in window) {
        (window as Window & { requestIdleCallback: (cb: () => void, opts?: { timeout: number }) => void })
          .requestIdleCallback(deferAnalytics, { timeout: 2500 });
      } else {
        setTimeout(deferAnalytics, 1);
      }
    };
    if (document.readyState === 'complete') {
      runWhenIdle();
    } else {
      window.addEventListener('load', runWhenIdle);
    }
  }, []);

  async function initializeFbPixel() {
    const { default: ReactPixel } = await import('react-facebook-pixel');
    const storedUser = localStorage.getItem('user');
    const user = storedUser ? JSON.parse(storedUser) : {};

    let advancedMatching = {
      em: '',
      fn: '',
      ln: '',
      ph: '',
      ge: '',
      db: '',
      ct: '',
      st: '',
      zp: '',
      country: '',
    }
    if (!isEmptyUser(user)) {
      advancedMatching = {
        em: isValidEmail(user.email) ? user.email : '',
        fn: user.name,
        ln: user.last_name,
        ph: user.phone,
        ge: user.gender,
        db: user.birthdate_date,
        ct: '',
        st: '',
        zp: '',
        country: 'AR',
      }
    }
     const options = {
       autoConfig: true, // set pixel's autoConfig. More info: https://developers.facebook.com/docs/facebook-pixel/advanced/
       debug: false, // enable logs
     };
     ReactPixel.init('1953784175025277', advancedMatching, options);
     ReactPixel.pageView();
  }

  async function initSentry() {
    const Sentry = await import('@sentry/react');
    Sentry.init({
      dsn: 'https://aa3bd04635c432e859a966b251f0edca@o4509753057673216.ingest.us.sentry.io/4509753080152064',
      integrations: [
        Sentry.captureConsoleIntegration({
          levels: ['error', 'warn', 'info'],
        }),
        Sentry.browserTracingIntegration(),
      ],
      sendDefaultPii: true,
      tracesSampleRate: 0.1,
      replaysSessionSampleRate: 0.1,  // 10% de sesiones grabadas
      replaysOnErrorSampleRate: 1.0,  // 100% si hubo error
    });

    const storedUser = localStorage.getItem('user');
    const user = storedUser ? JSON.parse(storedUser) : {};

    if (!isEmptyUser(user)) {
      Sentry.setUser({
        id: user.id,
        email: isValidEmail(user.email) ? user.email : null,
      });
    } else {
      Sentry.setUser(null);
    }
  }

  return (
      <LawyerThemeWrapper>
        <DefaultSEO />
        <RouteSEOWatcher />
        <StaticRouteSEO />
        <ScrollToTop />
        <DailyUpdateWrapper />
        <PushNotificationBanner />
        <Suspense fallback={<PageLoader />}>
          <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/legalbot" element={<LegalBot />} />
        <Route path="/legalbot/dashboard" element={<LegalBotDashboard />} />
        <Route path="/legalbot/conversations" element={<LegalBotConversations />} />
        <Route path="/legalbot/copilot" element={<LegalBotCopilot />} />
        <Route path="/legalbot/drafts/:draftId" element={<LegalBotLawsuitDraftEditor />} />
        <Route path="/legalbot/contract-drafts/:draftId" element={<LegalBotContractDraftEditor />} />
        <Route path="/legalbot/oficio-drafts/:draftId" element={<LegalBotOficioDraftEditor />} />
        <Route path="/legalbot/cases" element={<LegalBotCases />} />
        <Route path="/legalbot/cases/:id" element={<LegalBotCaseDetails />} />
        <Route path="/legalbot/configurations" element={<LegalBotConfigurations />} />
        <Route path="/legalbot/templates" element={<LegalBotTemplates />} />
        <Route path="/legalbot/pricing" element={<LegalBotPricing />} />
        <Route path="/legalbot/plan" element={<LegalBotPlan />} />
        <Route path="/legalbot/notifications" element={<LegalBotNotifications />} />
        <Route path="/legalbot/payment/finish" element={<LegalBotPaymentFinish />} />
        <Route path="/legalbot/tyc" element={<LegalBotTyC />} />
        <Route path="/legalbot/privacy-policy" element={<LegalBotPrivacyPolicy />} />
        <Route path="/legalbot/cookies" element={<LegalBotCookies />} />
        <Route path="/legalbot/lawyer/configurations" element={<LegalBotLawyerConfigurations />} />
        <Route path="/legalbot/lawyer/:id/profile" element={<PublicLawyerProfile />} />
        <Route path="/legalbot/lawyer/:id/edit" element={<EditLawyerProfile />} />
        <Route path="/legalbot/whatsapp/success" element={<WhatsAppSuccess />} />
        <Route path="/legalbot/whatsapp/failed" element={<WhatsAppFailed />} />
        <Route path="/legalbot/instagram/success" element={<InstagramSuccess />} />
        <Route path="/legalbot/instagram/failed" element={<InstagramFailed />} />
        <Route path="/legalbot/facebook/success" element={<FacebookSuccess />} />
        <Route path="/legalbot/facebook/failed" element={<FacebookFailed />} />
        <Route path="/legalbot/facebook/permisos" element={<FacebookPermisosTutorial />} />
        <Route path="/legalbot/influencers" element={<LegalBotInfluencers />} />
        {/* SEO programmatic: /abogado/<especialidad>-<localidad> (ej: /abogado/laboral-caba) */}
        <Route path="/abogado/perfil/:slug" element={<PublicLawyerProfile />} />
        <Route path="/abogado/:slug" element={<AbogadoEspecialidadLocalidad />} />

        {/* Locations SEO long-tail */}
        <Route path="/abogados" element={<LocationPage />} />
        <Route path="/abogados/:provincia" element={<LocationPage />} />
        <Route path="/abogados/:provincia/:ciudad" element={<LocationPage />} />

        <Route path="/how-work-lawyer" element={<HowWorkLawyer />} />
        <Route path="/how-work" element={<HowWorkClient />} />
        <Route path="/cookies" element={<Cookies />} />
        <Route path="/tyc" element={<TyC />} />
        <Route path="/faqs" element={<Faqs />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/about-us" element={<AboutUs />} />
        <Route path="/contact" element={<Contact />} />
        
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/payment/finish" element={<PaymentFinish />} />
        <Route path="/metrics" element={<Metrics />} />
        <Route path="/validate-lawyers" element={<ValidarAbogados />} />
        <Route path="/conversation_inspector" element={<ConversationInspector />} />
        <Route path="/conversation_inspector/:conversationId" element={<ConversationInspector />} />
        <Route path="/conversation_inspector/phone/:phoneNumber" element={<ConversationInspector />} />
        
        {/* Blog SEO long-tail */}
        <Route path="/blog/pagina-de-abogados" element={<PaginaDeAbogados />} />
        <Route path="/blog/abogado-argentina" element={<AbogadoArgentina />} />
        <Route path="/blog/mejor-pagina-de-abogados-en-argentina" element={<MejorPaginaAbogadosArgentina />} />
        <Route path="/blog/me-recibi-de-abogado" element={<MeRecibiDeAbogado />} />
        <Route path="/blog/me-recibi-de-abogada" element={<MeRecibiDeAbogado />} />
        <Route path="/blog/abogados-online" element={<AbogadosOnline />} />
        <Route path="/blog/abogado-de-familia" element={<AbogadoDeFamilia />} />
        <Route path="/blog/abogado-penalista" element={<AbogadoPenalista />} />
        <Route path="/blog/abogado-laboral" element={<AbogadoLaboral />} />
        <Route path="/blog/abogado-sucesiones" element={<AbogadoSucesiones />} />
        <Route path="/blog/mejores-abogados-argentina" element={<MejoresAbogados />} />
        <Route path="/blog/defensa-del-consumidor" element={<DefensaDelConsumidor />} />
        <Route path="/blog/reclamo-factura-internet-cable-telefonia" element={<ReclamoFacturaTelecomunicaciones />} />

        {/* Calculadoras legales (SEO + generación de leads) */}
        <Route path="/calculadora/indemnizacion-por-despido" element={<CalculadoraDespido />} />
        <Route path="/calculadora/cuota-alimentaria" element={<CalculadoraCuotaAlimentaria />} />
        <Route path="/calculadora/costos-sucesion" element={<CalculadoraSucesion />} />
        <Route path="/cuanto-cuesta-un-abogado" element={<CuantoCuestaAbogado />} />

        <Route path="/role-selection" element={<RoleSelection />} />
        <Route path="/user/registration" element={<SignIn />} />
        <Route path="/user/login" element={<LogIn />} />
        <Route path="/user/registration/finish" element={<UserRegistration />} />
        <Route path="/lawyer/registration/finish" element={<LawyerRegistration />} />
        <Route path="/user/forgot/password" element={<ForgotPassword />} />

        <Route path="/user/notifications" element={<NotificationsScreen />} />

        <Route path="/user/cases" element={<UserCases />} />
        <Route path="/user/cases/:id" element={<UserCaseDetail />} />
        <Route path="/user/case/summary" element={<CaseCreated />} />
        <Route path="/user/profile" element={<UserProfile />} />
        <Route path="/user/configurations" element={<UserConfiguration />} />
        <Route path="/user/chats" element={<UserChats />} />
        <Route path="/user/cases/create" element={<UserNewCase />} />

        <Route path="/marketplace" element={<LawyerMarketplace />} />
        <Route path="/lawyer/marketplace" element={<LawyerMarketplace />} />
        <Route path="/lawyer/cases/:id" element={<CaseDetail />} />
        {/*<Route path="/lawyer/cases" element={<LawyerCases />} />*/}
        <Route path="/lawyer/configurations" element={<LawyerConfiguration />} />
        <Route path="/lawyer/:id/profile" element={<PublicLawyerProfile />} />
        <Route path="/lawyer/:id/ratings" element={<LawyerRatings />} />
        <Route path="/lawyer/:id/edit" element={<EditLawyerProfile />} />
        <Route path="/lawyer/chats" element={<LawyerChats />} />
        <Route path="/lawyer/plan" element={<LawyerPlan />} />
        <Route path="/lawyer/directory" element={<LawyerDirectory />} />
        <Route path="/lawyer/directory/ranking" element={<LawyerDirectoryRanking />} />
        <Route path="/lawyer/clients" element={<Clients />} />
        <Route path="/lawyer/ai-studio" element={<AIStudio />} />
        <Route path="/lawyer/jurisprudence" element={<LawyerJurisprudence />} />

        {/* Ruta comodín: 404 real (noindex) en vez de redirigir a home (evita soft-404) */}
        <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
      </LawyerThemeWrapper>
  );
}

const DailyUpdateWrapper = () => {
  useCheckInfo();
  return null; // No renderiza nada
};

export default App;
